<template>
  <div class="task-details-item insurance-panel-section">
    <div class="task-details-item-header">
      <h5 class="section-title">
        <i class="fa fa-shield section-icon"></i>
        Insurance
      </h5>
      <span v-if="loading" class="ip-head-note">
        <i class="fa fa-spinner fa-spin" aria-hidden="true"></i> Loading…
      </span>
    </div>

    <div class="section-content">
      <!-- No insurance on file -->
      <div v-if="!loading && !plans.length" class="no-form-submissions">
        <div class="empty-state-icon"><i class="fa fa-shield"></i></div>
        <p class="empty-state-message">
          No insurance on file for <strong>{{ patientLabel }}</strong>.
        </p>
        <p class="empty-state-submessage">
          Add a plan in Open Dental and it will appear here.
        </p>
      </div>

      <template v-else-if="plans.length">
        <!-- Primary / Secondary tabs — rendered only when there IS a second plan, so a
             single-plan patient sees no tab strip at all. -->
        <div v-if="plans.length > 1" class="ip-tabs">
          <button
            v-for="p in plans"
            :key="p.ordinal"
            type="button"
            class="ip-tab"
            :class="{ 'is-active': activeOrdinal === p.ordinal }"
            @click="selectPlan(p.ordinal)"
          >
            {{ p.ordinal === 1 ? 'Primary' : 'Secondary' }}
          </button>
        </div>

        <div v-if="activePlan" class="ip-plan">
          <!-- Carrier + status -->
          <div class="ip-row ip-row--head">
            <div class="ip-carrier">
              <div class="ip-carrier-name">{{ activePlan.insurance.carrierName || 'Unknown carrier' }}</div>
              <div class="ip-member">
                Member ID: <span class="ip-mono">{{ activePlan.insurance.memberId || '—' }}</span>
              </div>
            </div>
            <span class="ip-status" :style="statusStyle">{{ statusLabel }}</span>
          </div>

          <!--
            Never verified: the grid below would be two em-dashes and the word "Never", which
            reads as BROKEN rather than as "no check has run yet". Say it in words instead and
            let the Verify button underneath carry the action.
          -->
          <div v-if="activePlan.neverVerified && !lastCheckLine" class="ip-nudge">
            No eligibility check has run for this plan yet. Verify to pull remaining benefits
            and the deductible from the carrier.
          </div>
          <!-- A check that ran but did not verify still gets the grid: "Never" + what happened. -->

          <!-- Benefits, straight off the stored verification. Nothing here is derived or
               estimated: an absent figure prints an em-dash rather than a guess. -->
          <div v-else class="ip-facts" :class="{ 'ip-facts--muted': benefitLines.muted }">
            <div class="ip-fact">
              <div class="ip-fact-k">Remaining benefits</div>
              <div class="ip-fact-v">{{ benefitLines.maxLine || '—' }}</div>
            </div>
            <!--
              ⛔ A null dedLine means the payer said NOTHING about a deductible — which is not
              the same as an unknown amount, and not the same as zero (benefitCellView returns
              the string 'No deductible' for a real zero). The dashboard table drops the line
              in that case; drop the whole cell here rather than invent a third behaviour.
              The grid is auto-fit, so it reflows to two columns on its own.
            -->
            <div v-if="benefitLines.dedLine" class="ip-fact">
              <div class="ip-fact-k">Deductible</div>
              <div class="ip-fact-v">{{ benefitLines.dedLine }}</div>
            </div>
            <div class="ip-fact">
              <div class="ip-fact-k">Last verified</div>
              <div class="ip-fact-v">{{ lastVerifiedLabel }}</div>
              <div v-if="lastCheckLine" class="ip-fact-note" data-test="insurance-last-check-note">
                {{ lastCheckLine }}
              </div>
            </div>
          </div>

          <!-- An OD record problem the office can fix for free. Rendered verbatim from the
               backend's preflight vocabulary — the same words the dashboard uses. -->
          <div v-if="activePlan.insurance.dataIssue" class="ip-issue">
            <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
            {{ activePlan.insurance.dataIssue.detail }}
          </div>

          <!--
            What happened to the check the office just ran — IN THE CARD, not only a toast.
            Toasts fade, and main.js `filterBeforeCreate` silently drops one when another of the
            same type is already showing, so on 2026-09-17 a failed Verify could leave the card
            exactly as it was before the click with nothing on screen saying why.
            Takes precedence over the stored-status hint below: this is the newer fact.
          -->
          <div
            v-if="outcomeLine"
            class="ip-issue"
            :class="{ 'ip-issue--muted': outcomeLine.tone === 'muted' }"
            data-test="insurance-verify-outcome"
          >
            <i v-if="followUpActive" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
            <i v-else class="fa fa-exclamation-triangle" aria-hidden="true"></i>
            {{ outcomeLine.text }}
          </div>
          <!-- Result of the last check, when it was not a clean verification. -->
          <div v-else-if="statusHint" class="ip-issue ip-issue--muted">{{ statusHint }}</div>

          <!-- ── Verify ─────────────────────────────────────────────────────────── -->
          <!--
            ⛔ EVERY click arms this confirm first. This panel sits on a page many roles
            open, and one click here is a real billed eligibility check. The confirm always
            names ONE plan and ONE check because the request always carries an explicit
            ordinal — so the sentence is literally true rather than approximately true.
          -->
          <div v-if="confirming" class="ip-confirm">
            <div class="ip-confirm-title">Run an eligibility check?</div>
            <div class="ip-confirm-facts">
              <div><span>Patient</span><strong>{{ patientLabel }}</strong></div>
              <div><span>Carrier</span><strong>{{ activePlan.insurance.carrierName || '—' }}</strong></div>
              <div><span>Member ID</span><strong>{{ activePlan.insurance.memberId || '—' }}</strong></div>
            </div>
            <p class="ip-confirm-cost">
              This runs <strong>1 eligibility check</strong> against
              {{ activePlan.insurance.carrierName || 'this carrier' }}
              ({{ activeOrdinal === 1 ? 'primary' : 'secondary' }} plan) and is billed
              whether or not the carrier finds the patient.
            </p>
            <div class="ip-confirm-actions">
              <button type="button" class="ip-btn ip-btn--ghost" @click="confirming = false">Cancel</button>
              <button type="button" class="ip-btn ip-btn--primary" :disabled="verifying" @click="confirmVerify">
                <i v-if="verifying" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                {{ verifying ? 'Checking…' : 'Run check' }}
              </button>
            </div>
          </div>

          <div v-else-if="canVerify || canViewBenefits" class="ip-actions">
            <button
              v-if="canVerify"
              type="button"
              class="ip-btn ip-btn--primary"
              :disabled="verifying || followUpActive || !activePlan.insurance.hasInsurance"
              @click="requestVerify"
            >
              <i v-if="verifying" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
              {{ verifying ? 'Checking…' : verifyLabel }}
            </button>

            <!--
              Opens the SAME drawer the dashboard opens, so benefits can be read here instead
              of in Open Dental's imaging PDF. Reading detail is free — /detail replays a
              STORED verification and never calls Stedi — which is why this button carries no
              confirm while the one beside it does.
            -->
            <button
              v-if="canViewBenefits"
              type="button"
              class="ip-btn ip-btn--ghost"
              @click="openBenefits"
            >
              <i class="fa fa-file-text-o" aria-hidden="true"></i>
              View benefits
            </button>
          </div>
        </div>
      </template>
    </div>

    <!--
      The dashboard's detail drawer, mounted here so a patient's benefits can be read from
      their own profile. Same component, same props, same store slice — NOT a second
      implementation, because two surfaces that render benefits from different code is exactly
      how this feature previously ended up disagreeing with itself about one row.

      It is `position: fixed` with its own scrim at z-index 1050. On the classic page that is
      enough: living inside this card does not constrain it (no transformed ancestor there —
      checked). On the new Patient Profile page this card sits in a tab that stays mounted and is
      hidden with `display: none`, which would hide a fixed child too — so `drawerOnBody` moves the
      host element under <body> (see mounted), and the drawer opens over whichever tab is showing.
    -->
    <div ref="drawerHost" class="ip-drawer-host" data-test="ip-drawer-host">
      <VerificationDrawer
        :open="drawerOpen"
        :row="drawerRow"
        :site-id="siteId"
        :timezone="timezone"
        :verify-state="null"
        :confirm-request="confirmRequest"
        :default-network-view="drawerNetworkView"
        @close="onDrawerClose"
        @retry="onDrawerRetry"
        @verify="onDrawerVerify"
        @load-plan="onDrawerLoadPlan"
      />
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { mapActions } from 'vuex';
import { readSettingValue } from '@/utils/siteCapability.js';
import {
  statusPresentation,
  benefitCellView,
  verifyReasonMessage,
  relativeFromNow,
  lastCheckNote,
  classifyVerifyError,
  transientVerifyMessage,
  planVerificationMark,
  makeVerifyFollowUp,
} from '@/pages/InsuranceVerification/components/constants';

// Shown while a verify that got NO answer is being followed up with free /patient-status reads.
const FOLLOW_UP_WAITING = "Waiting for the carrier's answer — checking for a recorded result (free)…";
const FOLLOW_UP_EXHAUSTED = 'No result was recorded for this check. Run it again.';
import VerificationDrawer from '@/pages/InsuranceVerification/components/VerificationDrawer.vue';

export default {
  name: 'InsurancePanel',
  /*
   * ⛔ NO `mixins: [mixin]` — THAT WAS THE 2026-09-17 BUG. Do not add it back.
   *
   * src/mixin.js defines COMPUTEDS named `patNum` (parsed from a route `patientID`, else
   * `$attrs.patNum`) and `loading` (the global alert flag). In Vue 2 a component built with
   * Vue.extend gets its computeds installed on its PROTOTYPE after its props, so a mixin
   * computed with a prop's name silently REPLACES the prop — dev builds only print
   * "The computed property "patNum" is already defined as a prop".
   * Here `this.patNum` was therefore always null: every "Run check" threw the store's pre-send
   * guard (`verifyPatient requires siteID and patNum`), never reached AWS, and was mislabelled
   * "still running". The `patNum` watcher never fired either, and `loading` read the global
   * alert flag instead of this card's prop.
   * The only thing this panel took from the mixin was the site flag — now a local computed.
   * Pinned by tests/component/InsurancePanel.spec.js (mounted: the dispatched patNum).
   */
  components: { VerificationDrawer },

  props: {
    siteId: { type: String, default: '' },
    patNum: { type: [String, Number], default: null },
    patientLabel: { type: String, default: 'this patient' },
    // Identity for the drawer, in the dashboard's `row.patient` shape — see
    // PatientProfile's `insurancePatient`. /patient-status does not carry it.
    patient: { type: Object, default: null },
    timezone: { type: String, default: '' },
    // The /patient-status payload. Owned by PatientProfile so the chip and this panel
    // render from ONE fetch and can never disagree about the same patient.
    status: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    // The new Patient Profile page keeps this card mounted inside a tab it hides with
    // `display: none`; true moves the drawer's host element under <body> so it can still open.
    // Absent (the classic page) = nothing moves.
    drawerOnBody: { type: Boolean, default: false },
  },

  data() {
    return {
      activeOrdinal: 1,
      confirming: false,
      // ONE lock for the whole card, not per plan. Two plans mean two buttons, and without
      // a shared lock a fast double-click across the tabs is two billed checks.
      verifying: false,
      drawerOpen: false,
      // Bumped to re-arm the drawer's own confirm — same handshake the dashboard uses.
      confirmRequest: 0,
      // Which plan the drawer is showing, so Retry re-fetches the slice that actually failed
      // instead of always re-fetching the primary.
      drawerOrdinalHint: 1,
      // The in-card line for the check just run: { ordinal (1|2|null = every plan), tone, text }.
      outcome: null,
      // A verify got NO answer and we are re-reading /patient-status (free) for its result.
      // ⛔ Verify stays DISABLED meanwhile: a 504'd check may still be running on the Lambda,
      // and a second click would be a second billed check for the same plan.
      followUpActive: false,
    };
  },

  created() {
    // Non-reactive on purpose — a timer handle has no business in Vue's reactivity.
    this._followUp = makeVerifyFollowUp({
      // The PARENT owns `status` (one fetch feeds the chip and this card). Ask it for a quiet
      // re-read; the `status` watcher below sees the answer.
      refresh: () => this.$emit('verified', { quiet: true }),
      onExhausted: () => {
        const ordinal = this.outcome ? this.outcome.ordinal : this.activeOrdinal;
        this.followUpActive = false;
        this.outcome = { ordinal, tone: 'warn', text: FOLLOW_UP_EXHAUSTED };
      },
    });

    // Tell the page about the PRIMARY plan, so its header chip can draw a Verify / Re-check button
    // that is a remote for THIS card (runPrimaryFromHeader). Immediate: the page needs the state as
    // soon as the card mounts, not at the next change. Nothing here dispatches anything.
    this.$watch(() => this.primaryVerifyState, (state) => this.$emit('primary-state', state), { immediate: true });
  },

  mounted() {
    // Vue patches the drawer's root inside the moved host (it resolves the insertion parent from
    // the old node), so the drawer then renders under <body>. Its events and props still flow
    // through this component — only the DOM position changes.
    if (this.drawerOnBody && this.$refs.drawerHost) document.body.appendChild(this.$refs.drawerHost);
  },

  beforeDestroy() {
    this._followUp.stop();
    // The host no longer lives inside this card's element, so removing the card would leave it
    // (and an open drawer's scrim) behind on <body>.
    const host = this.$refs.drawerHost;
    if (host && host.parentNode === document.body) document.body.removeChild(host);
  },

  computed: {
    /**
     * The per-site flag, read from the same `state.customer.settings` rows the mixin's
     * insuranceVerificationEnabled read ('TRUE' / 'FALSE', missing row = off), through the
     * shared settings reader.
     */
    insuranceVerificationEnabled() {
      const customer = (this.$store && this.$store.state && this.$store.state.customer) || {};
      return readSettingValue(customer.settings, 'INSURANCE_VERIFICATION_ENABLED') === 'TRUE';
    },

    plans() {
      return (this.status && Array.isArray(this.status.plans)) ? this.status.plans : [];
    },

    activePlan() {
      return this.plans.find((p) => p.ordinal === this.activeOrdinal) || this.plans[0] || null;
    },

    primaryPlan() {
      return this.plans.find((p) => p.ordinal === 1) || null;
    },

    secondaryPlan() {
      return this.plans.find((p) => p.ordinal === 2) || null;
    },

    /**
     * "View benefits" only appears when there is a STORED check to open. /detail resolves a
     * verification id; with no id the drawer would open onto its own empty state, which is a
     * worse answer than not offering the button.
     *
     * ⛔ Deliberately NOT gated on `canVerify`. That gate exists because the button beside it
     * SPENDS; reading a stored result does not. A role allowed to see this card is allowed to
     * read what the practice already paid for.
     */
    canViewBenefits() {
      const p = this.activePlan;
      return !!(p && p.verification && p.verification.verificationId);
    },

    /**
     * A dashboard-shaped row, assembled from /patient-status so the SAME VerificationDrawer
     * renders here. `buildPatientPlans` (lib/dashboard-assemble.js) gives each plan the exact
     * per-plan slice a dashboard row carries — insurance / verification / benefits /
     * lastVerified / isStale / isFresh / neverVerified — which is what makes this an assembly
     * rather than a fabrication.
     *
     * ⛔ The PRIMARY occupies the row's top level and the SECONDARY goes in `row.secondary`,
     * because that is how the drawer decides which plan a tab shows (`activePlanRow`). Putting
     * the active plan on top instead would show the secondary's benefits under "Primary" —
     * the same wrong-plan family of bug this feature has already hit three times.
     *
     * There is no `appointment` key on purpose: this patient may well have none, which is the
     * entire reason the panel exists. The drawer never reads one.
     */
    drawerRow() {
      if (!this.plans.length) return null;
      const base = this.primaryPlan || this.plans[0];
      const p = this.patient || {};
      return {
        patient: {
          patNum: this.patNum,
          fName: p.fName || '',
          lName: p.lName || '',
          dob: p.dob || '',
        },
        insurance: base ? base.insurance : null,
        verification: base ? base.verification : null,
        benefits: base ? base.benefits : null,
        odLastVerified: base ? base.odLastVerified : null,
        lastVerified: base ? base.lastVerified : null,
        lastChecked: base ? base.lastChecked : null,
        isStale: !!(base && base.isStale),
        isFresh: !!(base && base.isFresh),
        neverVerified: !!(base && base.neverVerified),
        secondary: this.secondaryPlan || null,
      };
    },

    /**
     * The office's default benefits view (settings blob `benefitsNetworkView`) for the drawer.
     * Read from the store's cached settings — only when they belong to THIS site — and 'both'
     * (today's drawer) until they have loaded or if they never do. See openBenefits.
     */
    drawerNetworkView() {
      const iv = (this.$store && this.$store.state && this.$store.state.insuranceVerification) || {};
      const s = iv.settings || {};
      if (s.siteID !== this.siteId || !s.value) return 'both';
      return s.value.benefitsNetworkView || 'both';
    },

    /**
     * The spend gate for the plan on screen. The four gates themselves live in canVerifyPlan(),
     * so the header's remote can ask the SAME question about the PRIMARY plan.
     */
    canVerify() {
      return this.canVerifyPlan(this.activePlan);
    },

    /**
     * What the new page's header chip needs to draw its Verify / Re-check button for the PRIMARY
     * plan, whatever tab this card has selected: { canVerify, canViewBenefits, label, busy,
     * verifying, carrierName, memberId, outcome }. Emitted as `primary-state` (see created).
     * `canViewBenefits` is about the PRIMARY (a stored verification id), NOT the active plan and
     * NOT canVerify: reading is free. `busy` is the card's own
     * double-billing lock (verifying OR a follow-up still waiting), so the chip and this card can
     * never disagree about whether a check is in flight. `outcome` is the in-card line for the
     * PRIMARY (ordinal 1, or an every-plan run) — null when the line belongs to the secondary.
     */
    primaryVerifyState() {
      const p = this.primaryPlan;
      const o = this.outcome;
      const ins = (p && p.insurance) || {};
      return {
        canVerify: this.canVerifyPlan(p),
        canViewBenefits: !!(p && p.verification && p.verification.verificationId),
        label: p && p.neverVerified ? 'Verify' : 'Re-check',
        busy: this.verifying || this.followUpActive,
        verifying: this.verifying,
        carrierName: ins.carrierName || '',
        memberId: ins.memberId || '',
        outcome: o && (o.ordinal === 1 || o.ordinal === null) ? { tone: o.tone, text: o.text } : null,
      };
    },

    verifyLabel() {
      return this.activePlan && this.activePlan.neverVerified ? 'Verify insurance' : 'Re-check insurance';
    },

    // The outcome line belongs to the plan it was run against; an every-plan run (drawer,
    // ordinal null) shows on either tab.
    outcomeLine() {
      const o = this.outcome;
      if (!o) return null;
      if (o.ordinal !== null && o.ordinal !== this.activeOrdinal) return null;
      return o;
    },

    statusLabel() {
      const p = this.activePlan;
      if (!p) return '';
      const pres = statusPresentation(p.verification.status, { hasInsurance: p.insurance.hasInsurance });
      return pres.label;
    },
    statusStyle() {
      const p = this.activePlan;
      if (!p) return {};
      const pres = statusPresentation(p.verification.status, { hasInsurance: p.insurance.hasInsurance });
      return { color: pres.color, background: pres.bg };
    },

    benefitLines() {
      const p = this.activePlan;
      if (!p) return { maxLine: null, dedLine: null };
      // The SAME formatter the dashboard column uses, so the two surfaces cannot word the
      // same numbers differently (and the frozen-sentinel normalization comes along).
      return benefitCellView(
        { annualMaximum: p.benefits && p.benefits.annualMaximum, deductible: p.benefits && p.benefits.deductible },
        { isStale: p.isStale }
      );
    },

    lastVerifiedLabel() {
      const p = this.activePlan;
      if (!p || !p.lastVerified) return 'Never';
      return relativeFromNow(p.lastVerified, this.timezone) || 'Never';
    },

    // "Checked 3 hours ago · couldn't reach <carrier>" when the latest check did not verify.
    lastCheckLine() {
      const p = this.activePlan;
      if (!p) return null;
      return lastCheckNote({
        verification: p.verification,
        lastVerified: p.lastVerified,
        lastChecked: p.lastChecked,
        carrierName: p.insurance && p.insurance.carrierName,
        tz: this.timezone,
      });
    },

    /**
     * A short line under the badge when the last check did not come back clean.
     *
     * ⚠ Deliberately NOT verifyReasonMessage(): that maps a /verify pre-flight `reason`
     * (blocked_bad_data, unmapped_carrier, …), and a STORED verification carries a
     * `status` instead. Passing a status in matches no case and silently returns null —
     * the sort of thing that looks like it works because the line just never renders.
     */
    statusHint() {
      const p = this.activePlan;
      const s = p && p.verification.status;
      if (!s || s === 'verified') return '';
      if (s === 'inactive') return 'The carrier reported this plan as inactive. Confirm coverage with the patient.';
      if (s === 'unable_to_verify') return "The carrier couldn't confirm this patient. Verify the Open Dental record, or call the payer.";
      if (s === 'error') return 'The last check failed to complete. Running it again is safe.';
      return '';
    },
  },

  watch: {
    // A new patient must never inherit the previous one's armed confirm — nor, now, the
    // previous one's OPEN DRAWER, which would be someone else's benefits under this
    // patient's name. Profile routes swap the patient without remounting this component.
    patNum() {
      this.activeOrdinal = 1;
      this.confirming = false;
      this.verifying = false;
      this.drawerOrdinalHint = 1;
      if (this.drawerOpen) {
        this.drawerOpen = false;
        this.resetDetail();
      }
      // …nor the previous patient's follow-up: its next tick would read (and its verdict would
      // describe) the wrong patient.
      this._followUp.stop();
      this.followUpActive = false;
      this.outcome = null;
    },

    // Each quiet re-read lands here. The first one that carries a NEW check for a watched
    // plan ends the wait — the card is already showing it, so just clear the line.
    // (It may be a check someone else ran in the same minute — the nightly runner, a dashboard
    // user. It is still this plan's latest result, which is what the card is for.)
    status(next) {
      if (!this.followUpActive) return;
      if (this._followUp.notify(next)) {
        this.followUpActive = false;
        this.outcome = null;
        if (this.$toast) this.$toast.info('The check finished — this card now shows its result.');
      }
    },
  },

  methods: {
    ...mapActions('insuranceVerification', [
      'verifyPatient', 'fetchDetail', 'resetDetail', 'markSecondaryNotVerified', 'fetchSettings',
    ]),

    /**
     * ⛔ The spend gate, and it is deliberately NOT mixin.canAccessInsuranceVerification.
     *
     * That gate has a Super-Admin bypass and NO CASL term, and its own comment says it is
     * safe only because it leads to the SOFT-gated /patient-status. This button calls
     * /verify, which is flag-GATED and spends money, so it uses the same pair the sidebar
     * uses: the site flag AND the CASL subject. An Outsourced user, or a role revoked via
     * Feature Permissions, must not get a spend button here.
     *
     * A method, not a computed, because the question is asked about a plan: the one on screen
     * (canVerify) and the PRIMARY one (the header's remote — primaryVerifyState,
     * runPrimaryFromHeader). Every spend path goes through these four gates.
     */
    canVerifyPlan(plan) {
      if (!this.insuranceVerificationEnabled) return false;
      if (typeof this.$can === 'function' && !this.$can('view', 'insurance-verification')) return false;
      // The per-site kill switch. /verify answers 403 when it is off, so showing the button
      // would be offering a guaranteed failure.
      if (this.status && this.status.onDemandEnabled === false) return false;
      return !!(plan && plan.insurance.hasInsurance);
    },

    selectPlan(ordinal) {
      if (this.activeOrdinal === ordinal) return;
      this.activeOrdinal = ordinal;
      // Switching plans under an open confirm would let the user authorise one plan and
      // spend on another.
      this.confirming = false;
    },

    // ── Benefits drawer ──────────────────────────────────────────────────────────────────
    /**
     * Open the benefits drawer.
     *
     * It always opens on PRIMARY, exactly as it does from the dashboard — the drawer resets
     * its own `activeOrdinal` to 1 and exposes no prop to pre-select a tab. A dual-plan
     * patient reaches the secondary through the drawer's own Primary/Secondary tabs, which
     * emit `load-plan` and land in `onDrawerLoadPlan` below. Pre-fetching the secondary here
     * would load a slice the drawer is not showing.
     */
    openBenefits() {
      if (!this.canViewBenefits || !this.drawerRow) return;
      this.drawerOrdinalHint = 1;
      this.drawerOpen = true;
      this.loadPlanDetail(1);
      this.loadNetworkViewSetting();
    },

    /**
     * The header chip's "View benefits": a free read (/detail replays a stored verification) of the
     * PRIMARY plan, whatever tab this card has selected — so it is NOT openBenefits(), whose gate is
     * the ACTIVE plan. Ungated by canVerify for the same reason openBenefits is. It never dispatches
     * a check and leaves `verifying` / `confirming` / `outcome` alone.
     */
    openPrimaryBenefits() {
      const p = this.primaryPlan;
      if (!(p && p.verification && p.verification.verificationId) || !this.drawerRow) return;
      this.drawerOrdinalHint = 1;
      this.drawerOpen = true;
      this.loadPlanDetail(1);
      this.loadNetworkViewSetting();
    },

    /**
     * The office may default the drawer to one network (Precheck settings → Default benefits
     * view). This page never loads the IV settings otherwise, so fetch them on first open —
     * cached per site by the store, so later opens cost nothing. ⛔ Never blocks or fails the
     * drawer: on any error it simply stays on 'both', which is what it showed before.
     */
    loadNetworkViewSetting() {
      if (!this.siteId) return;
      Promise.resolve()
        .then(() => this.fetchSettings({ siteID: this.siteId }))
        .catch(() => {});
    },

    /**
     * Fetch one plan's stored detail. Free — /detail replays a stored row and never calls
     * Stedi — so re-fetching on open costs a round-trip, not money.
     *
     * ⛔ Fetch by verification id, never by patNum alone. `IsLatest` is per
     * (SiteID, PatNum, PlanNum), so "the patient's latest" is ambiguous for a dual-plan
     * patient and resolves to whichever plan was checked last — which is how the secondary's
     * benefits end up rendered under the Primary tab.
     */
    loadPlanDetail(ordinal, explicitId = null) {
      const plan = ordinal === 2 ? this.secondaryPlan : this.primaryPlan;
      const verificationId = explicitId
        || (plan && plan.verification && plan.verification.verificationId)
        || null;

      if (ordinal === 2 && !verificationId) {
        // Never checked. Passing patNum instead would resolve the PRIMARY's latest check and
        // present it as the secondary's — worse than an empty state.
        this.markSecondaryNotVerified({ patNum: this.patNum });
        return;
      }
      if (!this.siteId || !verificationId) return;

      this.fetchDetail({
        siteID: this.siteId,
        patNum: this.patNum,
        verificationId,
        ...(ordinal === 2 ? { slot: 'secondary' } : {}),
      }).catch(() => { /* surfaced in the store slice → the drawer offers Retry */ });
    },

    onDrawerClose() {
      this.drawerOpen = false;
      // Clear the shared store slice so a re-open always loads fresh rather than flashing the
      // previous patient's benefits. The dashboard does the same on close.
      this.resetDetail();
    },

    onDrawerRetry() {
      this.loadPlanDetail(this.drawerOrdinalHint);
    },

    onDrawerLoadPlan(ordinal, explicitId = null) {
      if (ordinal !== 2) return;
      this.drawerOrdinalHint = 2;
      this.loadPlanDetail(2, explicitId);
    },

    /**
     * Verify from INSIDE the drawer. The drawer arms its own confirm before emitting, so this
     * is already authorised — it does not re-use this card's confirm.
     *
     * `payload.ordinal` is the scope the confirm named: 1 or 2 for a single plan, null for
     * every plan. It is forwarded verbatim, so what gets billed is what the user was shown.
     */
    async onDrawerVerify(payload) {
      if (this.verifying || !this.drawerRow) return;
      const ordinal = (payload && payload.ordinal) || null;
      if (this.followUpActive) {
        // The previous check may still land (see followUpActive). Refuse OUT LOUD — the drawer
        // already armed its own confirm, so a silent no-op would look like a dead button.
        if (this.$toast) this.$toast.info("Still waiting on the last check's result — try again in a moment.");
        return;
      }
      const marksBefore = this.verifyMarks(ordinal);
      this.outcome = null;
      this.verifying = true;
      try {
        const data = await this.verifyPatient({
          siteID: this.siteId,
          patNum: this.patNum,
          triggerType: 'on_demand',
          ...(ordinal !== null ? { ordinal } : {}),
        });

        if (data && data.ok) {
          if (this.$toast) this.$toast.success('Insurance verified');
        } else {
          this.reportNotVerified(data, ordinal);
        }

        // Re-read the drawer by the id /verify just returned — the only id guaranteed to be
        // the check that actually ran, rather than whatever this card's `status` prop still
        // holds from before it. The card's own refresh follows via @verified.
        //
        // ⛔ Shape matters: /verify echoes the primary as `data.verificationId` and the
        // secondary as `data.secondary.verificationId`. A SCOPED run (ordinal supplied) skips
        // the secondary pass entirely, so its own id arrives as `verificationId` even when the
        // plan checked was the secondary — hence the fallback below.
        const primaryId = (data && data.verificationId) || null;
        const secondaryId = (data && data.secondary && data.secondary.verificationId) || null;

        if (ordinal === 2) {
          this.loadPlanDetail(2, primaryId || secondaryId);
        } else {
          this.loadPlanDetail(1, primaryId);
          // An unscoped check re-ran the secondary too; refresh that slice rather than leave
          // a stale result under the Secondary tab. Detail reads are free.
          if (secondaryId) this.loadPlanDetail(2, secondaryId);
        }

        this.$emit('verified');
      } catch (err) {
        this.reportVerifyFailure(err, ordinal, marksBefore);
      } finally {
        this.verifying = false;
      }
    },

    requestVerify() {
      if (this.verifying || this.followUpActive) return;
      this.confirming = true;
    },

    async confirmVerify() {
      if (this.verifying || !this.activePlan) return;
      const ordinal = this.activeOrdinal;
      const marksBefore = this.verifyMarks(ordinal);
      this.outcome = null;
      this.verifying = true;
      this.confirming = false;
      try {
        const data = await this.verifyPatient({
          siteID: this.siteId,
          patNum: this.patNum,
          triggerType: 'on_demand',
          // ⛔ ALWAYS explicit. Omitting it checks BOTH plans = two billed checks, which
          // would make the confirm's "1 eligibility check" a lie.
          ordinal: this.activeOrdinal,
        });

        if (data && data.ok) {
          if (this.$toast) this.$toast.success('Insurance verified');
        } else {
          this.reportNotVerified(data, ordinal);
        }
        // Refresh from the one source that feeds both this panel and the chip.
        this.$emit('verified');
        return data;
      } catch (err) {
        this.reportVerifyFailure(err, ordinal, marksBefore);
      } finally {
        this.verifying = false;
      }
    },

    /**
     * The new Patient Profile page's header chip ("Verify" / "Re-check"), as a REMOTE for this
     * card: the page calls this on the mounted instance, so there is still ONE lock
     * (`verifying` / `followUpActive`) and ONE dispatch (confirmVerify).
     *
     * The chip armed its own confirm (patient, carrier, member ID, "1 eligibility check"), so
     * this is already authorised — the same handshake as onDrawerVerify, and it does not touch
     * this card's own confirm except to disarm it.
     *
     * ⛔ Always the PRIMARY plan, always an explicit ordinal: pointing activeOrdinal at 1 first
     * means confirmVerify sends `ordinal: 1` even while the Secondary tab is selected, so the
     * chip's "1 eligibility check against <carrier> (primary plan)" is literally true. The gate
     * is re-checked here — the chip's v-if is not the only thing between a click and the bill.
     *
     * When the check comes back with a stored result, the benefits drawer opens on it — the chip
     * sits on a tab that may not be this card's, so the answer has to come to the user. Reading
     * that result is FREE (/detail replays the stored verification, it never calls the payer).
     * It opens by the id /verify just returned, the same rule onDrawerVerify documents, and not
     * through openBenefits(): that needs a verification id already in the stale `status` prop,
     * which a never-verified plan does not have yet. No drawer when the card's line explains the
     * outcome instead (a pre-flight refusal, a transient failure) or a follow-up is waiting.
     */
    async runPrimaryFromHeader() {
      if (this.verifying || this.followUpActive) return undefined;
      if (!this.canVerifyPlan(this.primaryPlan)) return undefined;
      this.activeOrdinal = 1;
      this.confirming = false;
      const data = await this.confirmVerify();
      if (data && data.verificationId && !this.outcome && !this.followUpActive && this.drawerRow) {
        this.drawerOrdinalHint = 1;
        this.drawerOpen = true;
        this.loadPlanDetail(1, data.verificationId);
        this.loadNetworkViewSetting();
      }
      return data;
    },

    /**
     * Snapshot of the plans a check is about to cover — { [ordinal]: mark } — so the follow-up
     * can tell a NEW result from the one already on file. A never-verified plan snapshots as
     * null, which any stored check then counts as new.
     */
    verifyMarks(ordinal) {
      const ordinals = ordinal !== null && ordinal !== undefined
        ? [ordinal]
        : this.plans.map((p) => p.ordinal);
      const marks = {};
      ordinals.forEach((o) => { marks[o] = planVerificationMark(this.status, o); });
      return marks;
    },

    /**
     * /verify answered 200 with ok:false.
     *
     * ⛔ A TRANSIENT result is NOT "the carrier could not confirm coverage". status 'error' /
     * reason 'deadline_exhausted' means the payer was never reached (or never answered), and
     * that copy would send the front desk to question a patient whose coverage is fine.
     * isTransientFailure (via transientVerifyMessage) is the same rule the dashboard uses.
     */
    reportNotVerified(data, ordinal) {
      const carrier = this.activePlan && this.activePlan.insurance
        ? this.activePlan.insurance.carrierName
        : null;
      const transient = transientVerifyMessage(data, carrier);
      if (transient) {
        this.outcome = { ordinal, tone: 'warn', text: transient };
        if (this.$toast) this.$toast.warning(transient);
        return;
      }
      // Here `reason` IS the right argument — a live /verify response carries one.
      const msg = verifyReasonMessage(data && data.reason, data);
      // A pre-flight refusal (nothing sent) gets the line; a real payer answer does not — the
      // refreshed card shows that result, and statusHint already explains it.
      if (msg) this.outcome = { ordinal, tone: 'warn', text: msg };
      if (this.$toast) this.$toast.info(msg || 'The check completed but the carrier could not confirm coverage.');
    },

    /**
     * /verify threw. Only an `unknown` outcome — no answer, so the check MAY have run — starts
     * the free follow-up; everything else is a settled fact the line states plainly.
     *
     * ⛔ NEVER RETRIES /verify. The follow-up only re-reads /patient-status.
     */
    reportVerifyFailure(err, ordinal, marksBefore) {
      const c = classifyVerifyError(err);
      if (c.outcome === 'unknown') {
        this.outcome = { ordinal, tone: 'muted', text: FOLLOW_UP_WAITING };
        this.followUpActive = true;
        this._followUp.start(marksBefore);
        if (this.$toast) this.$toast.info('No answer from the check yet — this card will update if a result arrives.');
        return;
      }
      this.outcome = { ordinal, tone: 'warn', text: c.message };
      if (this.$toast) this.$toast[c.type === 'error' ? 'error' : c.type](c.message);
    },
  },
};
</script>

<style lang="scss" scoped>
// ─────────────────────────────────────────────────────────────────────────────────────────
// ⛔ THE CARD CHROME BELOW IS A DELIBERATE MIRROR OF THE PARENT'S. Read this before "tidying".
//
// PatientProfile.scss is loaded SCOPED (PatientProfile.vue:3205). In Vue 2 a child component's
// ROOT element carries BOTH scope ids, but its DESCENDANTS carry only this component's. So our
// root `.task-details-item` does pick up the parent's card rules (white bg, 12px radius, border,
// shadow) — which is why a card outline was visible — while `.task-details-item-header`,
// `.section-title`, `.section-icon`, `.section-content` and the `.no-form-submissions` empty
// state matched NOTHING. The panel rendered with an unpadded body, a floating unstyled title
// and a dark shield, next to sibling cards that looked correct.
//
// Values copied verbatim from PatientProfile.scss:401-437 and :1638-1670 (responsive override
// :1882-1893). If the parent's card design changes, change it here too.
//
// ⛔ Do NOT "fix" this by adding ::v-deep to the parent's `.task-details-item` rule. It is the
// smaller diff and the far worse one: it leaks the card chrome into every child component the
// page renders. Containment is the whole point.
//
// ⚠ The ROOT `.task-details-item` is deliberately NOT redefined here — it already applies, and
// redefining it is the one thing that could actually fight the parent.
//
// ⚠ This panel is currently the ONLY child component on the page reusing `.task-details-item`;
// every other card is inline markup in PatientProfile.vue, which is why nothing else hit this.
// Any future extracted card will hit it too.
// ─────────────────────────────────────────────────────────────────────────────────────────
.insurance-panel-section {
  .task-details-item-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #e2e8f0;
    background-color: #f8fafc;
    border-radius: 12px 12px 0 0;
    flex-wrap: wrap;
    gap: 12px;

    .section-title {
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
      color: #0f172a;
      display: flex;
      align-items: center;

      .section-icon {
        margin-right: 0.75rem;
        color: #3abf94;
        font-size: 1.125rem;
        width: 20px;
        text-align: center;
      }
    }
  }

  .section-content {
    padding: 20px 24px;
  }

  // The `.no-form-submissions` idiom used by "Treatment Plans Signed" and "Form Submissions" —
  // this card's immediate neighbours — so the three empty states match.
  .no-form-submissions {
    text-align: center;
    padding: 40px 20px;
    color: #64748b;

    .empty-state-icon {
      margin-bottom: 16px;

      i {
        font-size: 48px;
        color: #cbd5e1;
      }
    }

    .empty-state-message {
      font-size: 1rem;
      color: #334155;
      margin-bottom: 8px;
      line-height: 1.5;

      strong {
        color: #3abf94;
        font-weight: 600;
      }
    }

    .empty-state-submessage {
      font-size: 0.875rem;
      color: #64748b;
      margin-bottom: 0;
      line-height: 1.4;
    }
  }

  // ── Panel content ───────────────────────────────────────────────────────────────────────
  // Type sizes are rem and colours are the PAGE's slate scale (#0f172a / #334155 / #64748b /
  // #94a3b8 / #e2e8f0), not Insurance Verification's graphite. This card lives on Patient
  // Profile, so it should read as one of its cards. The teal accents that DO survive are the
  // ones carrying meaning: brand green on the primary action and on the active tab.
  .ip-head-note {
    font-size: 0.75rem;
    color: #64748b;
  }

  .ip-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
  }

  .ip-tab {
    padding: 6px 14px;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    color: #64748b;
    border-radius: 999px;
    font-size: 0.8125rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.12s ease, border-color 0.12s ease, color 0.12s ease;

    &:hover {
      color: #334155;
      border-color: #cbd5e1;
    }

    &.is-active {
      background: #e9f6f1;
      border-color: #bfe4d8;
      color: #0b5c49;
    }
  }

  .ip-row--head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
  }

  .ip-carrier-name {
    font-size: 0.9375rem;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.35;
  }

  .ip-member {
    margin-top: 4px;
    font-size: 0.8125rem;
    color: #64748b;
  }

  .ip-mono {
    font-variant-numeric: tabular-nums;
  }

  // ⛔ SHAPE ONLY. The colours arrive inline from statusPresentation(), which the dashboard and
  // the drawer also render from. Restyling them here would let one status read two different
  // ways on two screens — the exact defect class this feature has already hit twice.
  .ip-status {
    flex: none;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
  }

  // Hairline-cell grid — the drawer's `.iv-facts-grid` idiom (VerificationDrawer.vue:1785),
  // recoloured to this page's slate border so it belongs to the card it sits in. The figures
  // read as one unit instead of three strings floating in whitespace.
  .ip-facts {
    display: grid;
    // minmax(0,…) so a long benefit string cannot blow the column out (the grid trap
    // documented on the consent mapping table).
    grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
    gap: 1px;
    background: #e2e8f0;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    overflow: hidden;
  }

  .ip-fact {
    background: #ffffff;
    padding: 12px 14px;
    min-width: 0;
  }

  .ip-fact-k {
    font-size: 0.6875rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #94a3b8;
  }

  .ip-fact-v {
    margin-top: 4px;
    font-size: 0.875rem;
    font-weight: 600;
    color: #0f172a;
    word-break: break-word;
  }

  .ip-fact-note {
    margin-top: 2px;
    font-size: 0.75rem;
    color: #64748b;
    line-height: 1.35;
    word-break: break-word;
  }

  // Stale (30+ days): the figures are still shown, but they must not read like money confirmed
  // this morning. Same rule as the dashboard table's `.iv-ben--muted`
  // (VerificationTable.vue:530). The panel computed `muted` and then dropped it — a real bug.
  .ip-facts--muted {
    .ip-fact-v {
      color: #94a3b8;
      font-weight: 500;
    }
  }

  // Never verified — replaces the grid rather than filling it with em-dashes.
  .ip-nudge {
    padding: 12px 14px;
    background: #f8fafc;
    border: 1px dashed #e2e8f0;
    border-radius: 10px;
    font-size: 0.8125rem;
    color: #64748b;
    line-height: 1.5;
  }

  .ip-issue {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-top: 12px;
    padding: 9px 12px;
    background: #fef6e7;
    border: 1px solid #f5d99b;
    border-radius: 8px;
    font-size: 0.8125rem;
    color: #b45309;
    line-height: 1.45;

    &--muted {
      background: #f8fafc;
      border-color: #e2e8f0;
      color: #64748b;
    }
  }

  .ip-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 16px;
  }

  .ip-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 36px;
    padding: 0 15px;
    border-radius: 9px;
    font-size: 0.8125rem;
    font-weight: 600;
    cursor: pointer;

    &--primary {
      border: 1px solid #0f7259;
      background: #0f7259;
      color: #ffffff;

      &:not(:disabled):hover {
        background: #0b5c49;
      }
    }

    &--ghost {
      border: 1px solid #cbd5e1;
      background: #ffffff;
      color: #0b5c49;
    }

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }

  .ip-confirm {
    margin-top: 16px;
    padding: 14px 16px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
  }

  .ip-confirm-title {
    font-size: 0.875rem;
    font-weight: 700;
    color: #0f172a;
  }

  .ip-confirm-facts {
    margin: 10px 0;
    font-size: 0.8125rem;

    div {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      padding: 3px 0;
    }
    span {
      color: #64748b;
    }
    strong {
      color: #0f172a;
    }
  }

  .ip-confirm-cost {
    margin: 0 0 12px;
    font-size: 0.8125rem;
    color: #475569;
    line-height: 1.5;
  }

  .ip-confirm-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
}

// Mirror of PatientProfile.scss:1882-1893 — the parent's own responsive override, which cannot
// reach this component's descendants either.
@media (max-width: 768px) {
  .insurance-panel-section {
    .task-details-item-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      padding: 12px 16px;
    }

    .section-content {
      padding: 16px;
    }
  }
}
</style>
